import React from 'react';
import styled, { css } from 'styled-components';
import { C, hover, EASE_OUT } from '../kit';
import { Fireflies } from '../d/Fireflies';
import {
  CampfireIcon,
  DockIcon,
  TreasureIcon,
  HoleIcon,
  TrailIcon,
  HutIcon,
  TreeIcon,
  MudIcon,
  ShackIcon
} from './icons';

interface LandmarkDef {
  id: string;
  label: string;
  Icon: React.FC;
  top: number; // percentage
  left: number; // percentage
}

export const LANDMARKS: LandmarkDef[] = [
  { id: 'about', label: 'Campfire', Icon: CampfireIcon, top: 5, left: 30 },
  { id: 'takeover', label: 'The Dock', Icon: DockIcon, top: 16, left: 65 },
  { id: 'tokenomics', label: 'Treasure', Icon: TreasureIcon, top: 27, left: 25 },
  { id: 'dollarize', label: 'Black Hole', Icon: HoleIcon, top: 38, left: 75 },
  { id: 'roadmap', label: 'The Trail', Icon: TrailIcon, top: 49, left: 40 },
  { id: 'artist', label: "ndhaus's Hut", Icon: HutIcon, top: 60, left: 70 },
  { id: 'nfts', label: 'Gallery Tree', Icon: TreeIcon, top: 71, left: 20 },
  { id: 'memes', label: 'Meme Mud', Icon: MudIcon, top: 82, left: 60 },
  { id: 'comic', label: 'Comic Shack', Icon: ShackIcon, top: 93, left: 35 },
];

interface MapProps {
  onSelect: (id: string) => void;
}

export const SwampMap: React.FC<MapProps> = ({ onSelect }) => {
  return (
    <MapContainer className="chapter" id="map">
      <Fireflies />
      
      {/* Background Trail SVG */}
      <svg className="trail-line" viewBox="0 0 100 1000" preserveAspectRatio="none">
        <path 
          d="M30,50 Q80,100 65,160 T25,270 T75,380 T40,490 T70,600 T20,710 T60,820 T35,930 L40,1000" 
          fill="none" 
          stroke={C.ink} 
          strokeWidth="4" 
          strokeDasharray="12 12" 
          strokeLinecap="round" 
        />
      </svg>
      
      {LANDMARKS.map((lm) => (
        <LandmarkButton 
          key={lm.id} 
          style={{ top: `${lm.top}%`, left: `${lm.left}%` }}
          onClick={() => onSelect(lm.id)}
          aria-label={`Open ${lm.label} section`}
        >
          <div className="icon">
            <lm.Icon />
          </div>
          <span className="label">{lm.label}</span>
        </LandmarkButton>
      ))}
      
      <div className="water-ripples">
         {/* Little animated ripples in the swamp */}
      </div>
    </MapContainer>
  );
};

const MapContainer = styled.section`
  position: relative;
  width: 100%;
  height: 100dvh; width: 100%; scroll-snap-align: start; scroll-snap-stop: always; position: relative; box-sizing: border-box;
  background-color: #172842; /* deeper night swamp blue */
  background-image: linear-gradient(rgba(14, 27, 51, 0.2), rgba(14, 27, 51, 0.8)), url('/redesign2/scenes/swamp-empty.webp');
  background-size: cover;
  background-position: center top;
  overflow: hidden;
  border-bottom: 4px solid ${C.ink};

  .trail-line {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0.6;
  }
`;

const LandmarkButton = styled.button`
  position: absolute;
  transform: translate(-50%, -50%);
  background: none;
  border: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  z-index: 10;
  
  .icon {
    width: 50px;
    height: 50px;
    transition: transform 200ms ${EASE_OUT};
    filter: drop-shadow(2px 4px 0px rgba(14,27,51,0.5));
  }
  
  .label {
    margin-top: 8px;
    font-family: 'GROBOLD', sans-serif;
    font-size: 12px;
    color: ${C.white};
    text-shadow: 2px 2px 0 ${C.ink}, -2px -2px 0 ${C.ink}, 2px -2px 0 ${C.ink}, -2px 2px 0 ${C.ink};
    transition: transform 200ms ${EASE_OUT};
  }

  &:active .icon,
  &:active .label {
    transform: scale(0.95) translateY(2px);
  }

  ${hover(css`
    &:hover .icon {
      transform: scale(1.1) rotate(-5deg);
    }
  `)}
`;
